import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { Tag } from '../components/Tag';
import { PreVisualizacao, TemplateEditor } from '../components/TemplateEditor';
import {
  Campo,
  Carregando,
  ErroBox,
  Modal,
  ModalRodape,
  PageHeader,
  Vazio,
} from '../components/ui';
// Sub-caminho, e não `@mpa/shared`: só os sub-caminhos estão no alias do Vite,
// e a raiz passa no tsc para quebrar depois, no build.
import { MODELOS_PRONTOS, type ModeloPronto } from '@mpa/shared/modelos-de-template';
import type { BotaoTemplate } from '@mpa/shared/template-text';

interface Texto {
  version: number;
  header: string | null;
  body: string;
  footer: string | null;
  botoes: BotaoTemplate[] | null;
  variaveis: { nome: string; exemplo: string | null }[];
}

interface Template {
  id: string;
  internalName: string;
  metaName: string | null;
  category: string;
  metaCategory: string;
  locale: string;
  purpose: string | null;
  status: string;
  isBlueprint: boolean;
  sourceTemplateId: string | null;
  /** Qual modelo pronto gerou este template, se veio de um. */
  sourceModelId: string | null;
  whatsappAccountId: string | null;
  metaTemplateId: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  qualityScore: string | null;
  texto: Texto | null;
}

interface Lista {
  meus: Template[];
}

interface Conta {
  id: string;
  label: string;
  /** Só existe depois que o "Testar API" o buscou na Meta. */
  displayPhone: string | null;
  onboardingStatus: string;
  active: boolean;
}

const CORES_STATUS: Record<string, string> = {
  RASCUNHO: 'muted',
  ENVIADO_APROVACAO: 'info',
  APROVADO: 'success',
  REJEITADO: 'danger',
  PAUSADO: 'warning',
  DESATIVADO: 'muted',
};

const ROTULOS_STATUS: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  ENVIADO_APROVACAO: 'Em análise na Meta',
  APROVADO: 'Aprovado',
  REJEITADO: 'Rejeitado',
  PAUSADO: 'Pausado pela Meta',
  DESATIVADO: 'Desativado',
};

/** Estados em que o texto ainda pode ser mexido — espelha o servidor. */
const EDITAVEL = ['RASCUNHO', 'REJEITADO'];

/**
 * Templates da organização.
 *
 * A tela existe por uma restrição do WhatsApp que não dá para contornar: fora
 * da janela de 24 horas, só sai mensagem por template aprovado pela Meta. Como
 * a plataforma INICIA conversas, sem template aprovado ela não funciona — só
 * responde a quem escrever primeiro.
 */
export function Templates() {
  const cliente = useQueryClient();
  const { pode } = useAuth();
  /** `'vazio'` abre o editor em branco; um modelo abre com o texto dentro. */
  const [criando, setCriando] = useState<ModeloPronto | 'vazio' | null>(null);
  /** O modelo esperando a confirmação de envio à Meta. */
  const [confirmando, setConfirmando] = useState<ModeloPronto | null>(null);
  const [confirmandoLote, setConfirmandoLote] = useState(false);
  /** O que aconteceu com cada um do lote, mostrado depois que ele termina. */
  const [resultadoLote, setResultadoLote] = useState<{
    enviados: string[];
    falhas: { nome: string; motivo: string }[];
  } | null>(null);
  const [editando, setEditando] = useState<Template | null>(null);

  const consulta = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<Lista>('/templates'),
  });

  const contas = useQuery({
    queryKey: ['whatsapp'],
    queryFn: () => api<Conta[]>('/whatsapp/accounts'),
  });

  const invalidar = () => {
    cliente.invalidateQueries({ queryKey: ['templates'] });
    // A conta muda de estado junto: o primeiro aprovado a promove para PRONTA.
    cliente.invalidateQueries({ queryKey: ['whatsapp'] });
  };

  /**
   * Uma conta por cliente — o cadastro recusa a segunda. Por isso o envio
   * direto não precisa perguntar por qual número sai: só existe um.
   */
  const contasAtivas = (contas.data ?? []).filter((c) => c.active);
  const semConta = contas.isSuccess && contasAtivas.length === 0;
  /**
   * Diferente de `!semConta`: enquanto as contas carregam, `semConta` ainda é
   * falso e a lista já está vazia. Agir nesse instante mandaria um template
   * sem conta nenhuma.
   */
  const podeEscrever = contasAtivas.length > 0;
  /**
   * Escrever e submeter são permissões distintas (spec §5.2), e a separação é
   * deliberada: escrever o texto é reversível, mandar para a Meta não é —
   * gasta uma análise, prende o nome na WABA e, na categoria errada, custa
   * dinheiro por mensagem. Quem só pode escrever continua vendo "Criar
   * rascunho".
   */
  const podeSubmeter = pode('templates.submit');

  /**
   * Os modelos que esta organização já usou, pelo id de origem.
   *
   * Arquivado não conta: desativar um template é dizer que ele saiu de
   * circulação, e depois disso recomeçar do modelo é o caminho esperado.
   *
   * Rejeitado CONTA. O certo ali é corrigir o texto e reenviar o mesmo
   * template — que continua sendo o mesmo lá na Meta, com o histórico da
   * rejeição junto. Criar um segundo deixaria o rejeitado para trás e gastaria
   * outra análise para chegar no mesmo lugar.
   */
  const modelosUsados = new Map(
    (consulta.data?.meus ?? [])
      .filter((t) => t.sourceModelId && t.status !== 'DESATIVADO')
      .map((t) => [t.sourceModelId!, t]),
  );

  const modelosPendentes = MODELOS_PRONTOS.filter((m) => !modelosUsados.has(m.id));

  const criar = useMutation({
    mutationFn: (dados: Record<string, unknown>) =>
      api('/templates', { method: 'POST', body: dados }),
    onSuccess: () => {
      invalidar();
      setCriando(null);
    },
  });

  /**
   * Do modelo direto para a análise da Meta, num movimento.
   *
   * Duas chamadas, e não uma transação: se a submissão falhar, o rascunho fica.
   * Ele é útil — aparece em "Meus templates" com o botão de submeter, e a
   * pessoa tenta de novo sem reescrever nada. Desfazer a criação só porque a
   * Meta recusou a conexão seria jogar fora a parte que deu certo.
   */
  const enviarModelo = async (modelo: ModeloPronto) => {
    const rascunho = await api<{ id: string }>('/templates', {
      method: 'POST',
      body: {
        internalName: modelo.nome,
        category: modelo.categoria,
        locale: 'pt-BR',
        body: modelo.body,
        footer: modelo.footer ?? '',
        botoes: modelo.botoes ?? [],
        whatsappAccountId: contasAtivas[0]?.id,
        sourceModelId: modelo.id,
      },
    });

    await api(`/templates/${rascunho.id}/submeter`, { method: 'POST' });
  };

  const submeterModelo = useMutation({
    mutationFn: enviarModelo,
    onSuccess: () => {
      invalidar();
      setConfirmando(null);
    },
  });

  /**
   * O lote, um de cada vez.
   *
   * Sequencial e não `Promise.all`, por duas razões concretas. O nome na Meta é
   * único por conta, e quem o resolve consulta os existentes antes de gravar —
   * duas criações em paralelo podem ver o mesmo nome livre e colidir. E uma
   * rajada de submissões simultâneas é o tipo de coisa que a Meta limita.
   *
   * Nenhuma falha interrompe o resto: o que deu certo está feito, e insistir no
   * que falhou é decisão de quem está olhando.
   */
  const submeterTodos = useMutation({
    mutationFn: async (modelos: ModeloPronto[]) => {
      const enviados: string[] = [];
      const falhas: { nome: string; motivo: string }[] = [];

      for (const modelo of modelos) {
        try {
          await enviarModelo(modelo);
          enviados.push(modelo.nome);
        } catch (erro) {
          falhas.push({ nome: modelo.nome, motivo: (erro as Error).message });
        }
      }

      return { enviados, falhas };
    },
    onSuccess: (resultado) => {
      invalidar();
      setConfirmandoLote(false);
      setResultadoLote(resultado);
    },
  });

  /**
   * Trava os botões dos modelos enquanto qualquer envio corre.
   *
   * A lista de "já usados" vem do servidor e só se atualiza depois da resposta.
   * No intervalo, o cartão ainda mostra o botão — e um segundo clique ali
   * criaria o mesmo template outra vez. O servidor recusa (é lá que a regra
   * vale), mas travar evita o erro que a pessoa não entenderia ter causado.
   */
  const enviando = criar.isPending || submeterModelo.isPending || submeterTodos.isPending;

  const salvar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Record<string, unknown> }) =>
      api(`/templates/${id}`, { method: 'PATCH', body: dados }),
    onSuccess: () => {
      invalidar();
      setEditando(null);
    },
  });

  const submeter = useMutation({
    mutationFn: (id: string) => api(`/templates/${id}/submeter`, { method: 'POST' }),
    onSuccess: invalidar,
  });

  const sincronizar = useMutation({
    mutationFn: (id: string) => api(`/templates/${id}/sincronizar`, { method: 'POST' }),
    onSuccess: invalidar,
  });

  const desativar = useMutation({
    mutationFn: (id: string) => api(`/templates/${id}`, { method: 'DELETE' }),
    onSuccess: invalidar,
  });

  return (
    <>
      <PageHeader
        titulo="Templates"
        subtitulo="Fora da janela de 24 horas, o WhatsApp só entrega mensagem por template aprovado. É o que permite iniciar uma conversa."
        acoes={
          <button
            className="btn btn-primary"
            disabled={!podeEscrever}
            title={semConta ? 'Conecte uma conta de WhatsApp primeiro.' : undefined}
            onClick={() => setCriando('vazio')}
          >
            <i className="ti ti-plus" /> Escrever do zero
          </button>
        }
      />

      {(consulta.isPending || contas.isPending) && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}

      {[submeter, sincronizar, desativar].map(
        (m, i) => m.isError && <ErroBox key={i} erro={m.error} />,
      )}

      {semConta && (
        <div className="aviso" style={{ marginBottom: 16 }}>
          <i className="ti ti-plug-off" />
          <span>
            Nenhuma conta de WhatsApp conectada. O template é submetido por uma conta — conecte
            uma em <strong>Configuração → WhatsApp</strong> antes de começar.
          </span>
        </div>
      )}

      {consulta.data && (
        <>
          <h2 className="secao-titulo">Meus templates</h2>

          {consulta.data.meus.length === 0 ? (
            <Vazio
              icone="ti-template"
              titulo="Nenhum template seu ainda"
              descricao="Comece por um dos modelos abaixo. Sem template aprovado a plataforma não inicia conversa nenhuma — ela só responde a quem escrever primeiro."
            />
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {consulta.data.meus.map((t) => (
                <div key={t.id} className="card">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 16,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ minWidth: 240, flex: 1 }}>
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}
                      >
                        <strong style={{ fontSize: 14 }}>{t.internalName}</strong>
                        <Tag type={CORES_STATUS[t.status] ?? 'muted'}>
                          {ROTULOS_STATUS[t.status] ?? t.status}
                        </Tag>
                        {t.texto && t.texto.version > 1 && (
                          <Tag type="muted">versão {t.texto.version}</Tag>
                        )}
                      </div>

                      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                        <span className="mono">{t.metaName}</span> · {t.locale} · {t.metaCategory}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                      {EDITAVEL.includes(t.status) && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setEditando(t)}
                        >
                          <i className="ti ti-pencil" /> Editar
                        </button>
                      )}

                      {EDITAVEL.includes(t.status) && pode('templates.submit') && (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={submeter.isPending}
                          onClick={() => submeter.mutate(t.id)}
                        >
                          <i className="ti ti-send" />{' '}
                          {t.status === 'REJEITADO' ? 'Reenviar' : 'Submeter'}
                        </button>
                      )}

                      {t.metaTemplateId && (
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={sincronizar.isPending}
                          onClick={() => sincronizar.mutate(t.id)}
                        >
                          <i className="ti ti-refresh" /> Consultar a Meta
                        </button>
                      )}

                      <button
                        className="btn btn-secondary btn-sm"
                        title="Desativar"
                        onClick={() => desativar.mutate(t.id)}
                      >
                        <i className="ti ti-archive" />
                      </button>
                    </div>
                  </div>

                  {t.status === 'REJEITADO' && t.rejectionReason && (
                    <div className="form-erro" style={{ marginTop: 12, marginBottom: 0 }}>
                      <strong>A Meta rejeitou:</strong> {t.rejectionReason}
                      <div style={{ marginTop: 4 }}>
                        Corrija o texto e reenvie — o template continua sendo o mesmo lá.
                      </div>
                    </div>
                  )}

                  {t.status === 'ENVIADO_APROVACAO' && (
                    <div className="aviso" style={{ marginTop: 12 }}>
                      <i className="ti ti-clock" />
                      <span>
                        Em análise desde{' '}
                        {t.submittedAt && new Date(t.submittedAt).toLocaleString('pt-BR')}. A
                        plataforma volta a perguntar sozinha; o botão só adianta a consulta.
                      </span>
                    </div>
                  )}

                  {t.texto && (
                    /* Fechada, como nos modelos. O que a pessoa acompanha nesta
                       lista é o STATUS — em análise, aprovado, rejeitado —, e o
                       texto ela já leu quando submeteu. */
                    <PreVisualizacao
                      header={t.texto.header}
                      body={t.texto.body}
                      footer={t.texto.footer}
                      botoes={t.texto.botoes}
                      recolhivel
                      abertaInicialmente={false}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Os modelos vêm com o produto, em código — não são conteúdo que
              alguém precisa cadastrar antes. Ficam sempre visíveis, e não só
              quando a lista de cima está vazia: o segundo template (o lembrete)
              é tão necessário quanto o primeiro, e quem já criou o convite
              precisa achar o caminho de novo. */}
          <h2 className="secao-titulo" style={{ marginTop: 28 }}>
            Modelos prontos
          </h2>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 16,
              flexWrap: 'wrap',
              marginTop: -8,
              marginBottom: 12,
            }}
          >
            <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0, maxWidth: '62ch' }}>
              {podeSubmeter
                ? 'Textos escritos dentro das regras da Meta. “Submeter” manda direto para análise; “Editar antes” abre o texto para você ajustar.'
                : 'Textos escritos dentro das regras da Meta. Criam um rascunho seu, que alguém com permissão de envio submete à análise.'}
            </p>

            {/* Só aparece com mais de um pendente: com um só, o botão do
                cartão já faz o mesmo, e dois caminhos para a mesma ação é
                que geram a dúvida sobre qual dos dois faz o quê. */}
            {podeSubmeter && modelosPendentes.length > 1 && (
              <button
                className="btn btn-primary btn-sm"
                disabled={!podeEscrever || enviando}
                title={semConta ? 'Conecte uma conta de WhatsApp primeiro.' : undefined}
                onClick={() => setConfirmandoLote(true)}
                style={{ flex: 'none' }}
              >
                <i className="ti ti-send" /> Submeter os {modelosPendentes.length}
              </button>
            )}
          </div>

          <div style={{ display: 'grid', gap: 12 }}>
            {MODELOS_PRONTOS.map((m) => {
              const usado = modelosUsados.get(m.id);

              return (
              <div key={m.id} className="card" style={usado ? { opacity: 0.62 } : undefined}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 16,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ minWidth: 240, flex: 1 }}>
                    <strong style={{ fontSize: 14 }}>{m.nome}</strong>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                      {m.quando}
                    </div>
                  </div>

                  {/* Quem pode submeter vai direto à Meta; quem só pode
                      escrever para no rascunho. O botão diz qual dos dois vai
                      acontecer, em vez de prometer o mesmo para os dois.

                      Já usado, os dois somem: o caminho passa a ser mexer no
                      template que existe, logo acima nesta mesma tela. */}
                  {usado ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Tag type={CORES_STATUS[usado.status] ?? 'muted'}>
                        {ROTULOS_STATUS[usado.status] ?? usado.status}
                      </Tag>
                      <span style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>
                        já está em Meus templates
                      </span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                      {podeSubmeter && (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={!podeEscrever || enviando}
                          title={semConta ? 'Conecte uma conta de WhatsApp primeiro.' : undefined}
                          onClick={() => setConfirmando(m)}
                        >
                          <i className="ti ti-send" /> Submeter
                        </button>
                      )}
                      <button
                        className="btn btn-secondary btn-sm"
                        disabled={!podeEscrever || enviando}
                        title={
                          semConta
                            ? 'Conecte uma conta de WhatsApp primeiro.'
                            : 'Abre o texto para você ajustar antes de enviar.'
                        }
                        onClick={() => setCriando(m)}
                      >
                        <i className="ti ti-pencil" />{' '}
                        {podeSubmeter ? 'Editar antes' : 'Criar rascunho'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Fechada por padrão: são oito, e o que decide a escolha é o
                    "quando" logo acima — o texto só importa depois de decidir. */}
                <PreVisualizacao
                  body={m.body}
                  footer={m.footer ?? null}
                  botoes={m.botoes}
                  recolhivel
                  abertaInicialmente={false}
                />
              </div>
              );
            })}
          </div>
        </>
      )}

      {confirmandoLote && (
        <Modal
          titulo={`Mandar ${modelosPendentes.length} para a Meta`}
          descricao="Vão um de cada vez. Os que já estão em Meus templates ficam de fora."
          aoFechar={() => setConfirmandoLote(false)}
          largura={560}
        >
          <ul style={{ margin: '0 0 16px', paddingLeft: 20, fontSize: 14, lineHeight: 1.9 }}>
            {modelosPendentes.map((m) => (
              <li key={m.id}>
                {m.nome}{' '}
                <span style={{ color: 'var(--text-dim)', fontSize: 12.5 }}>
                  {m.body.trim().length} caracteres
                </span>
              </li>
            ))}
          </ul>

          <div className="aviso">
            <i className="ti ti-alert-triangle" />
            <span>
              Cada um gasta uma análise e prende um nome na sua conta. Não dá para cancelar
              depois de enviado.
            </span>
          </div>

          <ModalRodape>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setConfirmandoLote(false)}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              disabled={submeterTodos.isPending}
              onClick={() => submeterTodos.mutate(modelosPendentes)}
            >
              <i className="ti ti-send" />{' '}
              {submeterTodos.isPending ? 'Enviando um a um…' : 'Enviar todos para análise'}
            </button>
          </ModalRodape>
        </Modal>
      )}

      {/* O relatório do lote. Um aviso de "pronto!" esconderia que dois dos
          oito falharam — e falha parcial é o desfecho mais provável de todos. */}
      {resultadoLote && (
        <Modal
          titulo={resultadoLote.falhas.length ? 'Enviado, com exceções' : 'Tudo enviado'}
          descricao="A análise corre por conta da Meta. O status de cada um aparece em Meus templates."
          aoFechar={() => setResultadoLote(null)}
          largura={560}
        >
          {resultadoLote.enviados.length > 0 && (
            <p style={{ fontSize: 14, marginTop: 0 }}>
              <i className="ti ti-check" style={{ color: 'var(--success)' }} />{' '}
              <strong>{resultadoLote.enviados.length}</strong> em análise:{' '}
              {resultadoLote.enviados.join(', ')}.
            </p>
          )}

          {resultadoLote.falhas.length > 0 && (
            <div className="form-erro" style={{ display: 'grid', gap: 8 }}>
              {resultadoLote.falhas.map((f) => (
                <div key={f.nome}>
                  <strong>{f.nome}</strong> — {f.motivo}
                </div>
              ))}
            </div>
          )}

          <ModalRodape>
            <button className="btn btn-primary" onClick={() => setResultadoLote(null)}>
              Entendi
            </button>
          </ModalRodape>
        </Modal>
      )}

      {confirmando && (
        <Modal
          titulo="Mandar para a Meta"
          descricao="A análise costuma levar de alguns minutos a alguns dias. Enquanto ela corre, o texto não pode ser editado."
          aoFechar={() => setConfirmando(null)}
          largura={560}
        >
          {submeterModelo.isError && (
            <div className="form-erro">{(submeterModelo.error as Error).message}</div>
          )}

          <p style={{ fontSize: 14, marginTop: 0 }}>
            <strong>{confirmando.nome}</strong> sai pelo número{' '}
            <strong>
              {contasAtivas[0]?.displayPhone ?? contasAtivas[0]?.label ?? 'conectado'}
            </strong>
            .
          </p>

          <PreVisualizacao
            body={confirmando.body}
            footer={confirmando.footer ?? null}
            botoes={confirmando.botoes}
            recolhivel
            abertaInicialmente={false}
          />

          {/* O que não dá para desfazer, dito antes e não depois. O nome fica
              preso à conta mesmo se o template for rejeitado, e a análise
              gasta é contada pela Meta de qualquer jeito. */}
          <div className="aviso" style={{ marginTop: 16 }}>
            <i className="ti ti-alert-triangle" />
            <span>
              Não dá para cancelar depois de enviado. Se quiser mudar alguma palavra, use{' '}
              <strong>Editar antes</strong>.
            </span>
          </div>

          <ModalRodape>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmando(null)}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              disabled={submeterModelo.isPending}
              onClick={() => submeterModelo.mutate(confirmando)}
            >
              <i className="ti ti-send" />{' '}
              {submeterModelo.isPending ? 'Enviando…' : 'Enviar para análise'}
            </button>
          </ModalRodape>
        </Modal>
      )}

      {criando && (
        <Modal
          titulo={criando === 'vazio' ? 'Escrever do zero' : criando.nome}
          descricao="Nasce como rascunho aqui dentro. Nada chega à Meta antes de você clicar em Submeter."
          aoFechar={() => setCriando(null)}
          largura={640}
        >
          <TemplateEditor
            // O modelo entra como valor inicial de um formulário comum, e não
            // como texto travado: ele é ponto de partida, não contrato. Quem
            // conhece o próprio público vai querer mudar o tom.
            inicial={
              criando === 'vazio'
                ? undefined
                : {
                    internalName: criando.nome,
                    category: criando.categoria,
                    body: criando.body,
                    footer: criando.footer ?? '',
                    botoes: criando.botoes ?? [],
                  }
            }
            /* A conta entra por `camposExtras` porque é o único campo que a
               tela do cliente tem e a de blueprint não: blueprint não pertence
               a WABA nenhuma. O editor é o mesmo nas duas. */
            camposExtras={
              <Campo
                label="Conta de WhatsApp"
                name="whatsappAccountId"
                obrigatorio
                opcoes={contasAtivas.map((c) => ({
                  valor: c.id,
                  rotulo: c.displayPhone ? `${c.label} · ${c.displayPhone}` : c.label,
                }))}
                ajuda="A aprovação pertence a esta conta e não vale para outra."
              />
            }
            // O id do modelo viaja mesmo quando a pessoa edita o texto: é o que
            // impede o mesmo modelo de ser submetido de novo mais tarde, e o
            // nome já não serve para isso justamente porque ela pode mudá-lo
            // nesta tela.
            aoSalvar={(dados) =>
              criar.mutate({
                ...dados,
                sourceModelId: criando === 'vazio' ? undefined : criando.id,
              })
            }
            aoCancelar={() => setCriando(null)}
            salvando={criar.isPending}
            erro={criar.isError ? criar.error : undefined}
            rotuloSalvar="Criar rascunho"
          />
        </Modal>
      )}

      {editando && (
        <Modal
          titulo="Editar template"
          descricao={
            editando.status === 'REJEITADO'
              ? 'Salvar cria uma versão nova. A anterior continua guardada — é a única explicação do motivo da rejeição.'
              : 'Enquanto está em rascunho, o texto é editado por cima.'
          }
          aoFechar={() => setEditando(null)}
          largura={640}
        >
          <TemplateEditor
            inicial={{
              internalName: editando.internalName,
              category: editando.category,
              locale: editando.locale,
              purpose: editando.purpose ?? '',
              header: editando.texto?.header ?? '',
              body: editando.texto?.body ?? '',
              footer: editando.texto?.footer ?? '',
              botoes: editando.texto?.botoes ?? [],
            }}
            // O idioma some da edição: ele faz parte da identidade do template
            // na Meta e não muda depois de submetido.
            aoSalvar={(dados) => {
              const { locale: _ignorado, ...resto } = dados;
              salvar.mutate({ id: editando.id, dados: resto });
            }}
            aoCancelar={() => setEditando(null)}
            salvando={salvar.isPending}
            erro={salvar.isError ? salvar.error : undefined}
          />
        </Modal>
      )}
    </>
  );
}
